import { AlertCircle, ChevronRight, Download, Loader2 } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/segmented-control";
import { EXPORT_FILENAME_MAX_LENGTH, normalizeExportFilename } from "@/lib/export-filenames";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
} from "@/lib/processing-states";
import { cn } from "@/lib/utils";

export type ExportFormat = "pdf" | "csv" | "excel";

const FORMAT_LABELS: Record<ExportFormat, string> = { pdf: "PDF", csv: "CSV", excel: "Excel" };

export interface ExportFormatOption {
  value: ExportFormat;
  disabled?: boolean;
}

const PDF_ONLY: ReadonlyArray<ExportFormatOption> = [{ value: "pdf" }];

/** The last generated file for the selected deliverable and format. */
export type ExportLastFile =
  | { state: "ready"; generatedAt?: number; onDownload: (fileName: string) => void | Promise<void> }
  | { state: "preparing" }
  | {
      state: "failed";
      error?: string;
      /** The last successful file, when an earlier export completed before this one failed. */
      onDownloadPrevious?: (fileName: string) => void | Promise<void>;
    };

export interface ExportDialogProps<D extends string> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown under the title, e.g. the job name. */
  description?: string;
  /** Choices of what to export; the picker is hidden when there is one or none. */
  deliverables?: ReadonlyArray<SegmentedControlOption<D>>;
  deliverable?: D;
  onDeliverableChange?: (value: D) => void;
  /** Defaults to PDF only. */
  formats?: ReadonlyArray<ExportFormatOption>;
  format?: ExportFormat;
  onFormatChange?: (format: ExportFormat) => void;
  /** File extension for the selected format, without the dot. */
  extension: string;
  /** Saved or default name; the field resets to it when the dialog opens or it changes. */
  defaultFileName: string;
  /** Tool-specific settings, collapsed behind a single Options row. */
  options?: ReactNode;
  optionsSummary?: string;
  lastFile?: ExportLastFile | null;
  /** Explains why Export is unavailable, e.g. a reviewer choosing a server-generated format. */
  notice?: ReactNode;
  onExport: (fileName: string) => void | Promise<void>;
  isExporting?: boolean;
  exportDisabled?: boolean;
}

/** Maps a server export artifact's status to the dialog's last-file row. */
export function lastFileFromArtifact({
  artifact,
  canDownload,
  download,
  saveName,
  offerPrevious = true,
}: {
  artifact: { status: string; error?: string | null; generatedAt?: number | null } | null | undefined;
  canDownload: boolean;
  /** Downloads the newest completed file; resolves whether it started. */
  download: (fileName: string) => Promise<boolean>;
  /** Saves a renamed download on this artifact; only called when it is the file downloaded. */
  saveName?: (fileName: string) => Promise<void>;
  /** False when the download can't be tied to the selected deliverable after a failed retry. */
  offerPrevious?: boolean;
}): ExportLastFile | null {
  if (!artifact) return null;
  if (artifact.status === GLOBAL_PROCESSING_PENDING || artifact.status === GLOBAL_PROCESSING_PROCESSING) {
    return { state: "preparing" };
  }
  if (artifact.status === GLOBAL_PROCESSING_FAILED) {
    // The previous file belongs to an older artifact, so its name isn't saved here.
    return {
      state: "failed",
      error: artifact.error ?? undefined,
      onDownloadPrevious: canDownload && offerPrevious ? (fileName) => void download(fileName) : undefined,
    };
  }
  if (artifact.status === GLOBAL_PROCESSING_COMPLETED && canDownload) {
    return {
      state: "ready",
      generatedAt: artifact.generatedAt ?? undefined,
      onDownload: async (fileName) => {
        if (await download(fileName)) await saveName?.(fileName);
      },
    };
  }
  return null;
}

const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const dayFormatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

/** "4:34 PM" for today, otherwise "Oct 19". */
function formatGeneratedAt(timestamp: number) {
  const isToday = new Date(timestamp).toDateString() === new Date().toDateString();
  return (isToday ? timeFormatter : dayFormatter).format(timestamp);
}

/** The footer's start slot: download the last file, or show that one is being prepared. */
function LastFileAction({
  lastFile,
  formatLabel,
  fileName,
}: {
  lastFile: ExportLastFile;
  formatLabel: string;
  fileName: string;
}) {
  if (lastFile.state === "preparing") {
    return (
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
        Preparing {formatLabel}…
      </span>
    );
  }
  const onDownload = lastFile.state === "ready" ? lastFile.onDownload : lastFile.onDownloadPrevious;
  if (!onDownload) return null;
  const label = lastFile.state === "ready" ? `Last ${formatLabel}` : `Previous ${formatLabel}`;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!fileName}
      aria-label={`Download ${label}`}
      onClick={() => void onDownload(fileName)}
    >
      <Download />
      {label}
      {lastFile.state === "ready" && lastFile.generatedAt ? (
        <span className="font-normal text-muted-foreground">{formatGeneratedAt(lastFile.generatedAt)}</span>
      ) : null}
    </Button>
  );
}

/**
 * The one export surface for every tool: pick what and which format, name the file, adjust
 * tool options, then Export. Header and footer stay pinned; only the body scrolls.
 */
export function ExportDialog<D extends string>({
  open,
  onOpenChange,
  description,
  deliverables = [],
  deliverable,
  onDeliverableChange,
  formats = PDF_ONLY,
  format = "pdf",
  onFormatChange,
  extension,
  defaultFileName,
  options,
  optionsSummary,
  lastFile,
  notice,
  onExport,
  isExporting = false,
  exportDisabled = false,
}: ExportDialogProps<D>) {
  const formId = useId();
  const fileNameId = useId();
  const extensionId = useId();
  const [fileNameDraft, setFileNameDraft] = useState(defaultFileName);
  const [optionsOpen, setOptionsOpen] = useState(false);

  // Follow a changed saved or default name only while the field is untouched, so a name saved
  // by a teammate mid-edit doesn't overwrite typing; reset fully on open and deliverable change.
  const appliedDefaultRef = useRef(defaultFileName);
  useEffect(() => {
    setFileNameDraft((draft) => (draft === appliedDefaultRef.current ? defaultFileName : draft));
    appliedDefaultRef.current = defaultFileName;
  }, [defaultFileName]);
  useEffect(() => {
    setFileNameDraft(appliedDefaultRef.current);
  }, [open, deliverable]);

  const fileName = normalizeExportFilename(fileNameDraft);
  const formatLabel = FORMAT_LABELS[format];
  const canExport = Boolean(fileName) && !exportDisabled && !isExporting;

  return (
    <Dialog open={open} onOpenChange={(next) => !isExporting && onOpenChange(next)}>
      <DialogContent draggable={false} className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="border-b px-5 py-4 pe-12">
          <DialogTitle>Export</DialogTitle>
          <DialogDescription className={cn(!description && "sr-only")}>
            {description ?? "Choose what to export and name the file."}
          </DialogDescription>
        </DialogHeader>
        <form
          id={formId}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (canExport) void onExport(fileName);
          }}
        >
          <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-x-3 gap-y-3">
            {deliverables.length > 1 && deliverable && onDeliverableChange ? (
              <>
                <span className="text-sm font-medium">What</span>
                <SegmentedControl
                  aria-label="What to export"
                  value={deliverable}
                  onValueChange={onDeliverableChange}
                  options={deliverables}
                  className="w-full [&>*]:min-w-0 [&>*]:flex-1 [&>*]:truncate"
                />
              </>
            ) : null}
            {formats.length > 1 && onFormatChange ? (
              <>
                <span className="text-sm font-medium">Format</span>
                <SegmentedControl
                  aria-label="Format"
                  value={format}
                  onValueChange={onFormatChange}
                  options={formats.map(({ value, disabled }) => ({
                    value,
                    disabled,
                    label: FORMAT_LABELS[value],
                  }))}
                />
              </>
            ) : null}
            <Label htmlFor={fileNameId}>File Name</Label>
            <div className="relative min-w-0">
              <Input
                id={fileNameId}
                value={fileNameDraft}
                maxLength={EXPORT_FILENAME_MAX_LENGTH}
                aria-describedby={extensionId}
                className="pe-14"
                onChange={(event) => setFileNameDraft(event.target.value)}
              />
              <span
                id={extensionId}
                className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-sm text-muted-foreground"
              >
                .{extension}
              </span>
            </div>
          </div>

          {options ? (
            <Collapsible open={optionsOpen} onOpenChange={setOptionsOpen} className="rounded-lg border">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm font-medium"
                >
                  <ChevronRight
                    className={cn("size-4 shrink-0 transition-transform", optionsOpen && "rotate-90")}
                    aria-hidden="true"
                  />
                  Options
                  {optionsSummary ? (
                    <span className="ms-auto truncate text-xs font-normal text-muted-foreground">
                      {optionsSummary}
                    </span>
                  ) : null}
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 border-t px-3 py-3">{options}</CollapsibleContent>
            </Collapsible>
          ) : null}

          {notice ? <div className="text-xs text-muted-foreground">{notice}</div> : null}
          {lastFile?.state === "failed" ? (
            <p className="flex items-start gap-2 text-xs text-destructive">
              <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
              Last {formatLabel} failed{lastFile.error ? `: ${lastFile.error}` : "."}
            </p>
          ) : null}
        </form>
        <DialogFooter className="flex-row flex-wrap items-center justify-end border-t px-5 py-3">
          {lastFile ? (
            <div className="w-full sm:me-auto sm:w-auto">
              <LastFileAction lastFile={lastFile} formatLabel={formatLabel} fileName={fileName} />
            </div>
          ) : null}
          <Button type="button" variant="outline" disabled={isExporting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={formId} disabled={!canExport} aria-label={`Export ${formatLabel}`}>
            {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
            {isExporting ? "Exporting…" : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
